import dagre from '@dagrejs/dagre'
import type { Node, Edge } from 'reactflow'
import type { AgentNodeData } from './nodes/AgentNode'
import type { RouterNodeData } from './nodes/RouterNode'
import type { SubgraphNodeData } from './nodes/SubgraphNode'

const NODE_WIDTH = 200
const NODE_HEIGHT = 80
const SUBGRAPH_PADDING = 40

type AnyNodeData = AgentNodeData | RouterNodeData | SubgraphNodeData

const RAW_NODES: Node<AnyNodeData>[] = [
  // Subgraph frames
  {
    id: 'sg-strategy',
    type: 'subgraphNode',
    position: { x: 0, y: 0 },
    data: { label: 'Strategy Team', team: 'strategy' },
    style: { width: 320, height: 180 },
    className: 'nopan',
  },
  {
    id: 'sg-content',
    type: 'subgraphNode',
    position: { x: 0, y: 0 },
    data: { label: 'Content Team', team: 'content' },
    style: { width: 660, height: 380 },
    className: 'nopan',
  },
  {
    id: 'sg-visual',
    type: 'subgraphNode',
    position: { x: 0, y: 0 },
    data: { label: 'Visual Team', team: 'visual' },
    style: { width: 480, height: 260 },
    className: 'nopan',
  },

  // Orchestrator nodes
  {
    id: 'orch-init',
    type: 'agentNode',
    position: { x: 0, y: 0 },
    data: {
      label: 'Inicio',
      agentId: null,
      team: 'orchestrator',
      agentRole: 'orchestrator',
      description: 'Inicializa la ejecucion',
    },
  },
  {
    id: 'orch-router',
    type: 'routerNode',
    position: { x: 0, y: 0 },
    data: { label: 'Router', condition: 'include_research?' },
  },
  {
    id: 'orch-finalize',
    type: 'agentNode',
    position: { x: 0, y: 0 },
    data: {
      label: 'Finalizador',
      agentId: null,
      team: 'orchestrator',
      agentRole: 'orchestrator',
      description: 'Genera reporte final',
    },
  },

  // Strategy team nodes (inside sg-strategy)
  {
    id: 'strat-researcher',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING, y: SUBGRAPH_PADDING + 30 },
    parentNode: 'sg-strategy',
    extent: 'parent',
    data: {
      label: 'Investigador',
      agentId: 'tavily_researcher',
      team: 'strategy',
      agentRole: 'researcher',
      description: 'Tavily / Perplexity',
    },
  },

  // Content team nodes (inside sg-content)
  {
    id: 'ct-planner',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING, y: SUBGRAPH_PADDING + 30 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Planificador',
      agentId: 'content_planner',
      team: 'content',
      agentRole: 'planner',
      description: 'Estructura del articulo',
    },
  },
  {
    id: 'ct-writer',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING, y: SUBGRAPH_PADDING + 130 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Redactor',
      agentId: 'content_writer',
      team: 'content',
      agentRole: 'writer',
      description: 'Borrador y refinamiento',
    },
  },
  {
    id: 'ct-brand',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 220, y: SUBGRAPH_PADDING + 130 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Guard de Marca',
      agentId: 'content_brand_guard',
      team: 'content',
      agentRole: 'brand_guard',
      description: 'Tono STRTGY',
    },
  },
  {
    id: 'ct-disruption',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 440, y: SUBGRAPH_PADDING + 80 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Val. Disruption',
      agentId: 'disruption_validator',
      team: 'content',
      agentRole: 'validator',
      description: 'Sin contenido generico',
    },
  },
  {
    id: 'ct-quant',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 440, y: SUBGRAPH_PADDING + 170 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Val. Cuantitativo',
      agentId: 'quantitative_validator',
      team: 'content',
      agentRole: 'validator',
      description: 'Rigor de datos',
    },
  },
  {
    id: 'ct-alignment',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 440, y: SUBGRAPH_PADDING + 260 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Val. Alineamiento',
      agentId: 'alignment_validator',
      team: 'content',
      agentRole: 'validator',
      description: 'Tono human-centric',
    },
  },
  {
    id: 'ct-critic',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 220, y: SUBGRAPH_PADDING + 260 },
    parentNode: 'sg-content',
    extent: 'parent',
    data: {
      label: 'Critico',
      agentId: 'content_critic',
      team: 'content',
      agentRole: 'critic',
      description: 'Califica calidad (7.0+)',
    },
  },

  // Visual team nodes (inside sg-visual)
  {
    id: 'vis-brand',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING, y: SUBGRAPH_PADDING + 30 },
    parentNode: 'sg-visual',
    extent: 'parent',
    data: {
      label: 'Guard Visual',
      agentId: 'visual_brand_guard',
      team: 'visual',
      agentRole: 'brand_guard',
      description: 'Identidad visual',
    },
  },
  {
    id: 'vis-prompter',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 220, y: SUBGRAPH_PADDING + 30 },
    parentNode: 'sg-visual',
    extent: 'parent',
    data: {
      label: 'Generador de Prompts',
      agentId: 'image_prompter',
      team: 'visual',
      agentRole: 'image_prompter',
      description: 'Prompts de imagen',
    },
  },
  {
    id: 'vis-validator',
    type: 'agentNode',
    position: { x: SUBGRAPH_PADDING + 220, y: SUBGRAPH_PADDING + 130 },
    parentNode: 'sg-visual',
    extent: 'parent',
    data: {
      label: 'Validador Visual',
      agentId: 'visual_validator',
      team: 'visual',
      agentRole: 'validator',
      description: 'Coherencia estetica',
    },
  },
]

const RAW_EDGES: Edge[] = [
  // Orchestrator flow
  { id: 'e-init-router', source: 'orch-init', target: 'orch-router', type: 'smoothstep' },
  { id: 'e-router-strategy', source: 'orch-router', target: 'sg-strategy', label: 'con research', type: 'smoothstep', animated: false, style: { stroke: '#8B5CF6', strokeDasharray: '5,5' } },
  { id: 'e-router-content', source: 'orch-router', target: 'sg-content', label: 'directo', type: 'smoothstep' },
  { id: 'e-strategy-content', source: 'sg-strategy', target: 'sg-content', type: 'smoothstep', style: { stroke: '#8B5CF6' } },
  { id: 'e-content-visual', source: 'sg-content', target: 'sg-visual', type: 'smoothstep', style: { stroke: '#3B82F6' } },
  { id: 'e-visual-router', source: 'sg-visual', target: 'orch-router', label: 'sig. tarea', type: 'smoothstep', style: { stroke: '#94A3B8', strokeDasharray: '5,5' } },
  { id: 'e-router-end', source: 'orch-router', target: 'orch-finalize', label: 'fin', type: 'smoothstep' },

  // Content internal
  { id: 'e-ct-plan-write', source: 'ct-planner', target: 'ct-writer', type: 'smoothstep' },
  { id: 'e-ct-write-brand', source: 'ct-writer', target: 'ct-brand', type: 'smoothstep' },
  { id: 'e-ct-brand-dis', source: 'ct-brand', target: 'ct-disruption', type: 'smoothstep' },
  { id: 'e-ct-dis-quant', source: 'ct-disruption', target: 'ct-quant', type: 'smoothstep' },
  { id: 'e-ct-quant-align', source: 'ct-quant', target: 'ct-alignment', type: 'smoothstep' },
  { id: 'e-ct-align-critic', source: 'ct-alignment', target: 'ct-critic', label: 'OK', type: 'smoothstep' },
  { id: 'e-ct-align-refine', source: 'ct-alignment', target: 'ct-writer', label: 'refinar', type: 'smoothstep', style: { stroke: '#F59E0B', strokeDasharray: '4,4' } },
  { id: 'e-ct-critic-refine', source: 'ct-critic', target: 'ct-writer', label: '< 7.0', type: 'smoothstep', style: { stroke: '#F59E0B', strokeDasharray: '4,4' } },

  // Visual internal
  { id: 'e-vis-brand-prompt', source: 'vis-brand', target: 'vis-prompter', label: 'OK', type: 'smoothstep' },
  { id: 'e-vis-prompt-val', source: 'vis-prompter', target: 'vis-validator', type: 'smoothstep' },
  { id: 'e-vis-val-refine', source: 'vis-validator', target: 'vis-prompter', label: 'refinar', type: 'smoothstep', style: { stroke: '#F59E0B', strokeDasharray: '4,4' } },

  // Strategy internal
  { id: 'e-strat-done', source: 'strat-researcher', target: 'sg-content', type: 'smoothstep', style: { stroke: '#8B5CF6' } },
]

function layoutGraph(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', nodesep: 60, ranksep: 120, acyclicer: 'greedy' })

  nodes
    .filter((n) => !n.parentNode)
    .forEach((n) => {
      const w = (n.style?.width as number) ?? NODE_WIDTH
      const h = (n.style?.height as number) ?? NODE_HEIGHT
      g.setNode(n.id, { width: w, height: h })
    })

  edges
    .filter((e) => {
      const sourceIsRoot = !nodes.find((n) => n.id === e.source)?.parentNode
      const targetIsRoot = !nodes.find((n) => n.id === e.target)?.parentNode
      return sourceIsRoot && targetIsRoot
    })
    .forEach((e) => g.setEdge(e.source, e.target))

  dagre.layout(g)

  return nodes.map((n) => {
    if (n.parentNode) return n
    const node = g.node(n.id)
    if (!node) return n
    const w = (n.style?.width as number) ?? NODE_WIDTH
    const h = (n.style?.height as number) ?? NODE_HEIGHT
    return {
      ...n,
      position: {
        x: node.x - w / 2,
        y: node.y - h / 2,
      },
    }
  })
}

export function getStaticGraphData(): { nodes: Node[]; edges: Edge[] } {
  const layoutedNodes = layoutGraph(RAW_NODES, RAW_EDGES)
  return { nodes: layoutedNodes, edges: RAW_EDGES }
}
